import type { ReactNode } from "react";
import { SignIn } from "@clerk/react";

/**
 * Shared split layout for the four neutral login doors (AUTH-01..04).
 *
 * Layout mirrors the shadcn-admin login-03 pattern: Clerk <SignIn> on the LEFT,
 * a full-height marketing/brand panel on the RIGHT. The brand panel carries the
 * APP-level identity only (AMSaaS / AMCaaS) — never a provider theme (WL-03,
 * WL-06). Provider look-and-feel is applied post-login from provider settings.
 *
 * The path a user arrives on is only a hint; post-login routing is by resolved
 * capability + host (AUTH-05). These pages just present the right front door.
 */
export interface AuthShellProps {
  /** App brand shown on the panel + mobile header, e.g. "AMSaaS". */
  appName: string;
  /** One-line app strapline, e.g. "Application Management, as a Service". */
  appTagline: string;
  /** Brand logo (metallic mark on transparent bg). */
  logoSrc: string;
  logoSrc2x?: string;
  /** Hex accent used for the Clerk primary colour + small UI accents. */
  accent: string;
  /** Tailwind classes for the right panel background (gradient). */
  panelClassName: string;
  /** Audience badge text shown above the form, e.g. "Client sign-in". */
  audienceLabel: string;
  /** Big headline on the brand panel. */
  headline: string;
  /** Supporting paragraph under the headline. */
  blurb: string;
  /** Short testimonial / proof line. */
  quote: { text: string; author: string };
  /** Audience-specific links rendered under the form (cross-door, register…). */
  footer?: ReactNode;
}

export function AuthShell(props: AuthShellProps): JSX.Element {
  const {
    appName,
    appTagline,
    logoSrc,
    logoSrc2x,
    accent,
    panelClassName,
    audienceLabel,
    headline,
    blurb,
    quote,
    footer,
  } = props;

  const srcSet = logoSrc2x ? `${logoSrc} 1x, ${logoSrc2x} 2x` : undefined;

  return (
    <div className="grid min-h-full lg:grid-cols-2">
      {/* Left — Clerk sign-in */}
      <div className="flex items-center justify-center bg-background px-4 py-12">
        <div className="flex w-full max-w-sm flex-col items-center gap-8">
          {/* Mobile-only brand header (panel is hidden < lg) */}
          <div className="flex flex-col items-center gap-2 lg:hidden">
            <img
              src={logoSrc}
              srcSet={srcSet}
              alt={appName}
              className="h-12 w-auto object-contain dark:brightness-150"
            />
            <span className="text-sm font-medium text-muted-foreground">
              {appTagline}
            </span>
          </div>

          <div className="flex w-full flex-col items-center gap-6">
            <span
              className="inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium text-muted-foreground"
              style={{ borderColor: `${accent}40` }}
            >
              <span
                className="size-1.5 rounded-full"
                style={{ background: accent }}
              />
              {audienceLabel}
            </span>

            <SignIn
              routing="hash"
              fallbackRedirectUrl="/"
              appearance={{ variables: { colorPrimary: accent } }}
            />

            {footer && (
              <div className="w-full border-t pt-6 text-center text-sm text-muted-foreground">
                {footer}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Right — app brand panel */}
      <div
        className={`relative hidden flex-col justify-between overflow-hidden p-12 text-white lg:flex ${panelClassName}`}
      >
        {/* Soft accent glow */}
        <div
          aria-hidden
          className="pointer-events-none absolute -right-24 -top-24 size-96 rounded-full blur-3xl"
          style={{ background: accent, opacity: 0.18 }}
        />

        <div className="relative flex flex-col items-center gap-3 text-center">
          <img
            src={logoSrc}
            srcSet={srcSet}
            alt={appName}
            className="h-28 w-auto object-contain drop-shadow-2xl"
          />
          <p className="text-sm font-medium uppercase tracking-[0.2em] text-white/60">
            {appTagline}
          </p>
        </div>

        <div className="relative space-y-4">
          <h2 className="text-3xl font-semibold tracking-tight">{headline}</h2>
          <p className="max-w-md text-white/70">{blurb}</p>
        </div>

        <blockquote
          className="relative space-y-2 border-l-2 pl-4"
          style={{ borderColor: accent }}
        >
          <p className="text-white/90">“{quote.text}”</p>
          <footer className="text-sm text-white/60">— {quote.author}</footer>
        </blockquote>
      </div>
    </div>
  );
}
